import {
    RuleConditionSimilarTo,
    TransactionRuleConditionDefinition,
} from "@common/types/transactionRule";

export interface EffectiveSplitDescriptionParams {
    splitParentId: number | null;
    splitDescription: string | null;
    bankDescription: string | null;
}

// Returns the meaningful split label (original casing), or null when the row is not an
// effective split line — not a split, empty/whitespace splitDescription (Stripe writes
// "" — stripe-transaction.factory.ts:185), or a label that merely duplicates the bank
// description. Do NOT compare displayDescription: it is derived FROM splitDescription.
export function getEffectiveSplitDescription({
    splitParentId,
    splitDescription,
    bankDescription,
}: EffectiveSplitDescriptionParams): string | null {
    if (splitParentId == null) {
        return null;
    }
    const trimmedLabel = splitDescription?.trim();
    if (!trimmedLabel) {
        return null;
    }
    if (bankDescription?.trim().toLowerCase() === trimmedLabel.toLowerCase()) {
        return null;
    }
    return trimmedLabel;
}

// Lowercased comparison key for split-type equality (C partition, D guard).
export function toSplitKey(
    effectiveSplitDescription: string | null,
): string | null {
    return effectiveSplitDescription?.toLowerCase() ?? null;
}

export interface BuildSplitAwareDescriptionParams {
    base: string | null;
    splitLabel: string | null;
}

// Presentation rule: a NULL/blank base stays NULL — the label never substitutes for it.
export function buildSplitAwareDescription({
    base,
    splitLabel,
}: BuildSplitAwareDescriptionParams): string | null {
    const trimmedBase = base?.trim();
    if (!trimmedBase) {
        return null;
    }
    if (!splitLabel || trimmedBase.toLowerCase() === splitLabel.toLowerCase()) {
        return trimmedBase;
    }
    return `${trimmedBase} - ${splitLabel}`;
}

export interface ParsedSimilarToValue {
    normalizedDescriptionId: string;
    // undefined = legacy rule (whole cluster); null = plain rows; string = split type
    effectiveSplitKey: string | null | undefined;
}

export function parseSimilarToValue(
    value: string | RuleConditionSimilarTo,
): ParsedSimilarToValue {
    if (typeof value === "string") {
        return { normalizedDescriptionId: value, effectiveSplitKey: undefined };
    }
    return value;
}

// Narrows a rule-condition value to the split-keyed SIMILAR_TO object shape.
export function isRuleConditionSimilarTo(
    value: TransactionRuleConditionDefinition["value"],
): value is RuleConditionSimilarTo {
    return (
        typeof value === "object" &&
        value !== null &&
        "normalizedDescriptionId" in value &&
        "effectiveSplitKey" in value
    );
}
